<template>
  <div class="main container">
    <img src="@/assets/logos/mobials-long-purple.png" :alt="$t('mobialsLogoAlt')" id="logo" />
    <div class="row">
      <div class="col-lg-8 mb-2">
        <h4>{{ businessName }}</h4>
        <p v-show="businessName">
          {{ $t('creditCardCapture.header') }}
        </p>
        <alert
          :show-alert="alertFields.showAlert"
          :message="alertFields.message"
          :variant="alertFields.variant"
          :inline="true"
          :is-dismissible="false"
          @dismissAlert="dismissAlert"
        />
        <div id="zuora_payment" v-show="!alertFields.showAlert && !creditCardSubmitted" />
      </div>
      <div class="col-lg-4 mb-2">
        <div class="border p-4" id="help_box">
          <h6 class="mb-4" id="help_box_header">
            {{ $t('creditCardCapture.helpBoxHeader') }}
          </h6>
          <p class="mb-4" id="help_box_body">
            {{ $t('creditCardCapture.helpBoxBody') }}
          </p>
          <div id="help_number">
            <a href="tel:18778331423">1 (877) 833-1423</a>
          </div>
          <div id="help_email">
            <a href="mailto:accounting@mobials.com">accounting@mobials.com</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadScript } from 'vue-plugin-load-script';
import businessService from '@/service/businessService';
import Alert from '@/app/global/layout/Alert.vue';
import variants from '@/utils/enums/alertVariants';
import log from '@/service/log';
import { setLocale } from '@/utils/i18nHelpers';

// This global thing is needed since ESLint will complain about Z being an undefined variable and throw an error during tests
/*
  global Z
*/

export default {
  name: 'CreditCardCapturePage',
  components: {
    Alert,
  },
  data() {
    return {
      masterBusinessId: null,
      businessName: null,
      cccParamsLoaded: false,
      zuoraScriptLoaded: false,
      creditCardSubmitted: false,
      // By not specifying supportedTypes and paymentGateway it will use the defaults that are set for this tenant
      zuoraPaymentPageParameters: {
        tenantId: null,
        id: null,
        token: null,
        signature: null,
        style: 'inline',
        key: null,
        submitEnabled: 'true',
        locale: null,
        url: null,
        field_accountId: null,
      },
      alertFields: {
        showAlert: false,
        variant: null,
        message: {
          msgHasLink: false,
          msg1: '',
          msg2: '',
          msgLinkText: '',
          msgLink: '',
        },
      },
    };
  },
  computed: {
    readyToRenderIFrame() {
      return this.zuoraScriptLoaded && this.cccParamsLoaded;
    },
  },
  watch: {
    readyToRenderIFrame(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.renderIFrame();
      }
    },
  },
  created() {
    this.$store.commit('SET_LOADING', true, { root: true });
    this.masterBusinessId = this.$route.params.masterBusinessId;

    // Can't rely on scss to resize based on window (with @media) because the iFrame does it's own resizing as well
    window.addEventListener('resize', this.updateZuoraIFrameHeight);

    this.loadZuoraScript();
    this.getCCCParams();
  },
  unmounted() {
    window.removeEventListener('resize', this.updateZuoraIFrameHeight);
  },
  methods: {
    loadZuoraScript() {
      loadScript('https://static.zuora.com/Resources/libs/hosted/1.3.1/zuora-min.js')
        .then(() => {
          this.zuoraScriptLoaded = true;
        })
        .catch(error => {
          this.$store.commit('SET_LOADING', false, { root: true });
          this.showErrorAlert(this.$t('alerts.generalError'));
          log(error);
        });
    },
    getCCCParams() {
      businessService
        .getCCCParams(this.masterBusinessId)
        .then(response => {
          this.setCCCParams(response);
        })
        .catch(error => {
          this.$store.commit('SET_LOADING', false, { root: true });
          this.showErrorAlert(this.$t('alerts.generalError'));
          log(error);
        });
    },
    setCCCParams(response) {
      setLocale(response.data.locale);
      this.businessName = response.data.businessName;

      this.zuoraPaymentPageParameters.id = response.data.hostedPaymentPageId;
      this.zuoraPaymentPageParameters.url = response.data.hostedPaymentPageUrl;
      this.zuoraPaymentPageParameters.locale = response.data.locale;
      this.zuoraPaymentPageParameters.field_accountId = response.data.zuoraAccountId;

      this.zuoraPaymentPageParameters.tenantId = response.data.rsaSignature.tenantId;
      this.zuoraPaymentPageParameters.token = response.data.rsaSignature.token;
      this.zuoraPaymentPageParameters.signature = response.data.rsaSignature.signature;
      this.zuoraPaymentPageParameters.key = response.data.rsaSignature.key;

      this.cccParamsLoaded = true;
    },
    renderIFrame() {
      Z.setEventHandler('onloadCallback', () => {
        // Need to call update right away (even before any resizing happens) because Zuora's iFrame loads with 0 height initially for some reason
        this.updateZuoraIFrameHeight();
        this.$store.commit('SET_LOADING', false, { root: true });
      });

      Z.render(this.zuoraPaymentPageParameters, {}, this.cardCaptureCallback);
    },
    async cardCaptureCallback(response) {
      this.$store.commit('SET_LOADING', true, { root: true });
      this.creditCardSubmitted = true;

      if (response.success) {
        await this.successfulCardCaptureCallback(response.refId);
      } else {
        // Ignoring the error returned by zuora since it is not easy to translate for each error code, and probably unnecessary.
        // For now, will instead display a generic error
        this.$store.commit('SET_LOADING', false, { root: true });
        this.showErrorAlert(this.$t('creditCardCapture.creditCardUpdateError'));
        log(new Error(`Message: ${response.errorMessage} Code: ${response.errorCode}`));
      }
    },
    successfulCardCaptureCallback(paymentMethodId) {
      businessService
        .successfulCardCaptureCallback(this.masterBusinessId, paymentMethodId)
        .then(() => {
          this.showSuccessAlert(this.$t('creditCardCapture.creditCardUpdateSuccess'));
        })
        .catch(error => {
          // If an error is returned, then it means 2 things:
          // - the credit card was not set as the default payment method and autopay was not set to true (zuora workflow)
          // - HubSpot fields for credit card were not updated
          // Note that the credit card was still saved successfully
          this.showErrorAlert(this.$t('alerts.generalError'));
          log(error);
        })
        .finally(() => {
          this.$store.commit('SET_LOADING', false, { root: true });
        });
    },
    showSuccessAlert(message) {
      this.alertFields.variant = variants.SUCCESS;
      this.alertFields.message.msg1 = message;
      this.alertFields.showAlert = true;
    },
    showErrorAlert(message) {
      this.alertFields.variant = variants.DANGER;
      this.alertFields.message.msg1 = message;
      this.alertFields.showAlert = true;
    },
    dismissAlert() {
      this.alertFields.showAlert = false;
      this.alertFields.message = {
        msgHasLink: false,
        msg1: '',
        msg2: '',
        msgLinkText: '',
        msgLink: '',
      };
    },
    updateZuoraIFrameHeight() {
      const iFrame = document.getElementById('z_hppm_iframe');

      if (iFrame.offsetWidth <= 767) {
        iFrame.style.minHeight = '860px';
      } else {
        iFrame.style.minHeight = '650px';
      }
    },
  },
};
</script>

<style scoped lang="scss">
// General layout

// Desktop layout
.main {
  position: relative;
  margin-top: 25px;
}

#logo {
  max-width: 200px;
  min-width: 150px;
}

#help_box {
  #help_box_header {
    font-size: 1rem;
  }

  #help_box_body {
    font-size: 0.9rem;
  }

  #help_number a {
    font-weight: 500;
    color: black;
    font-size: 1.1rem;
  }

  #help_email a {
    font-weight: 500;
    font-size: 0.9rem;
  }
}

// Tablet
@media screen and (max-width: 1200px) {
  #logo {
    min-width: 150px;
  }
}

// Mobile
@media screen and (max-width: 767px) {
  #logo {
    max-width: 25%;
    min-width: 120px;
  }
}
</style>
